/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { ActivatedRoute } from '@angular/router';

import {
  CELL_NO_OVERFLOW_CLASS,
  CELL_TEXT_RIGHT_CLASS,
  defaultCurrencyCellRendererDecorator,
  outerLabelCellRendererDecorator,
} from '@gh/common-internal-shared';
import { AppRouter, getRouteInitData, NumberTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  buildNumberColumn,
  cellRendererSelectorForPinnedRow,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  typedValueFormatter,
} from '@gh/core-ui-grid';

import {
  calculateExpenseTotals,
  JobFinancialsExpense,
  JobFinancialsExpensesFilterCode,
  JobFinancialsExpenseTotals,
} from '../../../resources/job-financials.model';

@Component({
  standalone: false,
  selector: 'gh-job-financials-expense-list',
  templateUrl: 'job-financials-expense-list.component.html',
  styleUrls: ['job-financials-expense-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobFinancialsExpenseListComponent implements OnInit, OnChanges {
  expensesGridDefinition: LayoutBasedGridDefinition;
  expensesTotals: JobFinancialsExpenseTotals[];
  jobFinancialsExpensesFilterCode = JobFinancialsExpensesFilterCode;

  @Input() jobId: ID;
  @Input() jobFinancialsExpenseList: JobFinancialsExpense[];
  @Input() filter: JobFinancialsExpensesFilterCode = JobFinancialsExpensesFilterCode.isAllExpenses;

  @Output() filterChange = new EventEmitter<string>();

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private gridDefinitionFactory: GridDefinitionFactory,
              private translationService: TranslationService) {
    this.expensesGridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      budgetCost: {
        ...buildNumberColumn(NumberTypes.Price),
        cellClassRules: {
          [CELL_NO_OVERFLOW_CLASS]: 'true',
          [CELL_TEXT_RIGHT_CLASS]: 'true',
        },
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          outerLabelCellRendererDecorator(
            this.translationService.get('lbl_total_all_cost_centers'),
            20)(defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice)))),
      },
      actualCost: {
        ...buildNumberColumn(NumberTypes.Price),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice))),
      },
      variance: {
        ...buildNumberColumn(NumberTypes.Price),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice))),
      },
    },                                                                        {
      getRowId: (params) => String(params.data.costCenterId),
    });
  }

  ngOnInit(): void {
    const { expensesLayout } = getRouteInitData(this.route);
    this.expensesGridDefinition.bind(expensesLayout);
    this.expensesTotals = calculateExpenseTotals(this.jobFinancialsExpenseList);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.prototype.hasOwnProperty.call(changes, 'jobFinancialsExpenseList')) {
      this.expensesTotals = calculateExpenseTotals(this.jobFinancialsExpenseList);
    }
  }

  onTypeFilterChange({ value }: MatRadioChange): void {
    this.filterChange.emit(value);
  }
}
